import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { formatPrice } from '../../lib/cart';
import {
  customerSegment,
  customerKpis,
  filterCustomers,
  SEGMENT_LABELS,
  type CustomerRow,
  type Segment,
} from '../../lib/customers';

/**
 * Section « Clients (CRM) » de l'admin (parité legacy Customers tab) — base des
 * CLIENTS FINAUX du resto : segmentation (VIP / Régulier / Nouveau / Dormant),
 * KPIs, recherche, dépensé & commandes. Données depuis customer_tenants × customers.
 */
const SEG_STYLE: Record<Segment, string> = {
  vip: 'bg-amber-100 text-amber-800',
  regular: 'bg-emerald-100 text-emerald-800',
  new: 'bg-sky-100 text-sky-800',
  churning: 'bg-orange-100 text-orange-700',
  lost: 'bg-slate-200 text-slate-600',
  lead: 'bg-rose-100 text-rose-700',
};

export default function CustomersManager({ tenant }: { tenant: Tenant }) {
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState<Segment | 'all'>('all');
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<CustomerRow | null>(null);

  const reload = useCallback(async () => {
    const { data } = await supabase
      .from('customer_tenants')
      .select('customer_id, total_orders, total_spent_cents, loyalty_opt_in, marketing_consent, last_stamp_at, customers(email, first_name, phone)')
      .eq('tenant_id', tenant.id);
    const list: CustomerRow[] = ((data as unknown as Record<string, unknown>[]) ?? []).map((r) => {
      const c = (r.customers ?? {}) as { email?: string; first_name?: string; phone?: string };
      return {
        customerId: String(r.customer_id ?? ''),
        email: c.email ?? '',
        name: c.first_name ?? (c.email ? c.email.split('@')[0] : 'Client'),
        phone: c.phone ?? null,
        totalOrders: typeof r.total_orders === 'number' ? r.total_orders : 0,
        totalSpentCents: typeof r.total_spent_cents === 'number' ? r.total_spent_cents : 0,
        loyaltyOptIn: r.loyalty_opt_in === true,
        marketingConsent: r.marketing_consent === true,
        lastActivity: typeof r.last_stamp_at === 'string' ? r.last_stamp_at : null,
      };
    });
    // + Leads « Panier abandonné » (parité legacy) : commandes en ligne non payées
    // (client parti au paiement) = contacts à relancer, dédupliqués par email/tél.
    const { data: ab } = await supabase
      .from('orders')
      .select('customer_email, customer_name, customer_phone, created_at')
      .eq('tenant_id', tenant.id)
      .eq('payment_method', 'online')
      .neq('payment_status', 'paid')
      .eq('status', 'recue')
      .order('created_at', { ascending: false })
      .limit(200);
    const seen = new Set(list.map((r) => (r.email || '').toLowerCase()));
    const leads: CustomerRow[] = [];
    ((ab as { customer_email?: string; customer_name?: string; customer_phone?: string; created_at?: string }[]) ?? []).forEach((o) => {
      const email = (o.customer_email ?? '').toLowerCase();
      const key = email || (o.customer_phone ?? '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      leads.push({
        customerId: 'lead:' + key,
        email: o.customer_email ?? '',
        name: o.customer_name ?? 'Client',
        phone: o.customer_phone ?? null,
        totalOrders: 0,
        totalSpentCents: 0,
        loyaltyOptIn: false,
        marketingConsent: false,
        lastActivity: o.created_at ?? null,
        isLead: true,
      });
    });
    setRows([...list, ...leads]);
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const kpis = useMemo(() => customerKpis(rows), [rows]);
  const filtered = useMemo(() => filterCustomers(rows, { segment, query }), [rows, segment, query]);

  if (loading) return <p className="opacity-70">Chargement des clients…</p>;

  const segTabs: { id: Segment | 'all'; label: string }[] = [
    { id: 'all', label: 'Tous' },
    { id: 'vip', label: 'VIP' },
    { id: 'regular', label: 'Réguliers' },
    { id: 'new', label: 'Nouveaux' },
    { id: 'churning', label: 'À risque' },
    { id: 'lost', label: 'Perdus' },
    { id: 'lead', label: 'Paniers abandonnés' },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Clients" value={String(kpis.count)} />
        <Kpi label="VIP" value={String(kpis.vip)} />
        <Kpi label="Chiffre d'affaires" value={formatPrice(kpis.totalRevenueCents, tenant.currency)} />
        <Kpi label="Panier moyen" value={formatPrice(kpis.avgBasketCents, tenant.currency)} />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {segTabs.map((s) => (
            <button
              key={s.id}
              onClick={() => setSegment(s.id)}
              className={
                'rounded-lg px-3 py-1.5 text-sm font-semibold ' +
                (segment === s.id ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
              }
              style={segment === s.id ? { background: 'var(--brand-primary)' } : {}}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher (nom, email)…"
          className="ml-auto rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Segment</th>
              <th className="px-3 py-2 text-right">Cdes</th>
              <th className="px-3 py-2 text-right">Dépensé</th>
              <th className="px-3 py-2 text-center">Fidélité</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const seg = customerSegment(r);
              return (
                <tr key={r.customerId} onClick={() => setDetail(r)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={'rounded-full px-2 py-0.5 text-xs font-semibold ' + SEG_STYLE[seg]}>{SEGMENT_LABELS[seg]}</span>
                  </td>
                  <td className="px-3 py-2 text-right">{r.totalOrders}</td>
                  <td className="px-3 py-2 text-right">{formatPrice(r.totalSpentCents, tenant.currency)}</td>
                  <td className="px-3 py-2 text-center">{r.loyaltyOptIn ? '🎴' : '—'}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                  Aucun client {rows.length > 0 ? 'pour ce filtre' : 'pour le moment'}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        {filtered.length} client(s) affiché(s) sur {rows.length}. Les clients apparaissent dès leur première commande
        ou inscription fidélité. Cliquez une ligne pour la fiche détaillée.
      </p>

      {detail && <CustomerDetail row={detail} tenant={tenant} onClose={() => setDetail(null)} />}
    </div>
  );
}

/** Fiche client détaillée (parité legacy) : KPIs, consentements, fidélité, export RGPD. */
function CustomerDetail({ row, tenant, onClose }: { row: CustomerRow; tenant: Tenant; onClose: () => void }) {
  const seg = customerSegment(row);
  const avg = row.totalOrders > 0 ? Math.round(row.totalSpentCents / row.totalOrders) : 0;

  const exportRgpd = () => {
    const payload = {
      export: 'RGPD — données client',
      genere_le: new Date().toISOString(),
      restaurant: { nom: tenant.name, slug: tenant.slug },
      client: { nom: row.name, email: row.email, telephone: row.phone },
      relation: {
        segment: SEGMENT_LABELS[seg],
        commandes: row.totalOrders,
        total_depense_cents: row.totalSpentCents,
        panier_moyen_cents: avg,
        derniere_activite: row.lastActivity,
        fidelite_opt_in: row.loyaltyOptIn,
        consentement_marketing: row.marketingConsent,
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rgpd_${(row.email || row.customerId).replace(/[^a-z0-9]/gi, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">{row.name}</h3>
            <p className="text-sm text-slate-500">{row.email}{row.phone ? ` · ${row.phone}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          <Stat label="Commandes" value={String(row.totalOrders)} />
          <Stat label="Dépensé" value={formatPrice(row.totalSpentCents, tenant.currency)} />
          <Stat label="Panier moyen" value={formatPrice(avg, tenant.currency)} />
        </div>

        <ul className="mb-3 flex flex-col gap-1 text-sm">
          <li className="flex justify-between"><span className="text-slate-500">Segment</span><span className="font-semibold">{SEGMENT_LABELS[seg]}</span></li>
          <li className="flex justify-between"><span className="text-slate-500">Dernière activité</span><span>{row.lastActivity ? new Date(row.lastActivity).toLocaleDateString('fr-FR') : '—'}</span></li>
          <li className="flex justify-between"><span className="text-slate-500">Fidélité</span><span>{row.loyaltyOptIn ? '🎴 Inscrit' : 'Non inscrit'}</span></li>
          <li className="flex justify-between"><span className="text-slate-500">Consentement marketing</span><span>{row.marketingConsent ? '✓ Oui' : '✕ Non'}</span></li>
        </ul>

        <button onClick={exportRgpd} className="w-full rounded-xl border border-slate-300 py-2.5 text-sm font-semibold hover:bg-slate-50">
          📥 Export RGPD (JSON)
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold">{value}</div>
    </div>
  );
}
